import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Votre message a été envoyé avec succès !');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Erreur lors de l\'<envoi du message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4" data-testid="contact-page-title">
            Contactez-Nous
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="name">
                    Nom complet *
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                    placeholder="Jean Dupont"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="email">
                    Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                    placeholder="jean.dupont@example.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="phone">
                    Téléphone
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                    placeholder="06 61 83 71 37"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="service">
                    Service concerné
                  </label>
                  <select
                    id="service"
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                    data-testid="contact-service-select"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="panneaux-solaires">Panneaux Solaires</option>
                    <option value="pompes">Pompes Électromécaniques</option>
                    <option value="maintenance">Maintenance & SAV</option>
                    <option value="conseil">Conseil Énergétique</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  className="w-full bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4 py-3 resize-none"
                  placeholder="Décrivez votre projet ou posez votre question..."
                  required
                  data-testid="contact-message-textarea"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="contact-submit-btn"
              >
                <Send className="w-5 h-5" />
                {loading ? 'Envoi en cours...' : 'Envoyer le message'}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6" data-testid="contact-info-email">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-medium text-lg mb-2">Email</h3>
              <p className="text-sm text-muted-foreground">contact@solarinnov.fr</p>
            </div>

            <div className="bg-white rounded-xl p-6" data-testid="contact-info-phone">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-medium text-lg mb-2">Téléphone</h3>
              <p className="text-sm text-muted-foreground">06 61 83 71 37</p>
            </div>

            <div className="bg-white rounded-xl p-6" data-testid="contact-info-address">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-medium text-lg mb-2">Adresse</h3>
              <p className="text-sm text-muted-foreground">
                MEKNES<br />
                Maroc
              </p>
            </div>

            <div className="bg-primary text-white rounded-xl p-6">
              <h3 className="font-heading font-medium text-lg mb-2">Horaires d'ouverture</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Lundi - Vendredi</span>
                  <span>8h - 18h</span>
                </div>
                <div className="flex justify-between">
                  <span>Samedi</span>
                  <span>9h - 12h</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Dimanche</span>
                  <span>Fermé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};